import React, { Component } from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import { Machine } from "xstate";
import { Container, Row, Col } from "react-grid-system";
import { color, media } from "./../style/Variables.js";
import { withCopy } from "./../CopyProvider.js";
import { Trigger } from "./IDs.js";
import Statechart, { Helper as StatechartHelper } from "./Statechart.js";
import classnames from "classnames";
import HistoryView from "./HistoryView.js";

import * as ComparableProducts from "./ComparableProducts.js";
import { Logo } from "../Blocks.js";
import Markdown from "react-remarkable";

import icoBack from "./../img/ico-back.svg";
import icoClose from "./../img/ico-close.svg";

const HISTORY_UNDO_TO = "HISTORY_UNDO_TO";
const HISTORY_CLEAR = Trigger.NEUSTARTEN;

const UndoButton = styled.div.attrs({
  className: p =>
    classnames({
      disabled: p.disabled,
    }),
})`
  padding: 5px;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  width: 3em;
  height: 3em;
  position: absolute;
  z-index: 300;
  border-radius: 50%;
  left: 10px;
  top: 10px;
  ${media.sm`
    width: 3em;
    height: 3em;
    left: 1px;
    top: 1px;
  `};
  transition: all 0.4s ease;
  background: ${color.white};
  &.disabled {
    background: transparent;
    opacity: 0.1;
    cursor: default;
    * {
      cursor: default;
    }
  }
  &:not(.disabled):hover {
    cursor: pointer;
    opacity: 1;
    background: ${color.very_light_pink};
  }
`;

const CloseButton = styled(UndoButton)`
  left: auto;
  right: 10px;
  img {
    width: 14px;
  }
  ${media.sm`
    left: auto;
    right: 1px;
    img {
      width: 13px;
    }
  `};
`;

const Card = styled.div`
  position: relative;
  background: ${color.white};
  box-shadow: 0 12px 24px 0 rgba(0, 0, 0, 0.3);
  padding: 0 40px;
  border-radius: 2px;
  transition: all 0.5s ease;
  margin-bottom: 4em;
  button {
    min-width: 10rem;
    min-height: 48px;
    margin-bottom: 1rem;
  }
  @media (min-width: 601px) {
    margin-top: 1vh;
    max-width: 66%;
    padding: 50px;
  }
  @media (min-height: 1001px) {
    margin-top: 8vh;
  }
  @media (max-width: 600px) {
    padding-top: 8px;
    max-width: 96vw;
    box-sizing: padding-box;
    button {
      min-width: 6em;
      margin-left: 7px !important;
      margin-right: 7px !important;
    }
  }
  @media (max-width: 320px) {
    padding-top: 10px;
    padding-bottom: 10px;
    padding-left: 1rem;
    padding-right: 1rem;
  }
  select {
    min-height: 48px;
  }
  button {
    margin-left: 10px;
    margin-right: 10px;
  }
  .trigger-count-3 {
    flex-flow: column nowrap;
    align-items: center;
    button {
      width: 100%;
      max-width: 25em;
      margin-right: 0 !important;
      margin-left: 0 !important;
    }
  }
`;

class Wizard extends Component {
  static contextTypes = {
    copy: PropTypes.object,
  };
  //
  // States here are derived from the state machine.
  // They can be seen as computed properties of the instance.
  //
  machine = Machine(Statechart);
  state = {
    currentState: this.machine.initialState,
    triggers: StatechartHelper.getAvailableTriggers(this.machine.initialState),
    history: [{ state: this.machine.initialState }],
  };
  //
  // updateHistory: don't use this directly
  //
  updateHistory = action => {
    const history = this.state.history;
    switch (action.type) {
      case HISTORY_CLEAR:
        return [];
      case HISTORY_UNDO_TO:
        const turncated = history.slice(action.index);
        // clear the choice of the reverted item
        turncated[0].choice = null;
        return turncated;
      default:
        const appended = [
          // insert the new one
          { state: action.state },
          ...history,
        ];
        if (appended.length > 1) {
          // modify the last one
          appended[1].choice = action.type;
        }
        return appended;
    }
  };
  //
  //
  // Use these high-level functions
  //
  //
  dispatch = (action, callback) => {
    const newState = this.machine.transition(
      this.state.currentState,
      action.type
    );
    this.setState(
      {
        currentState: newState,
        triggers: StatechartHelper.getAvailableTriggers(newState),
        history: this.updateHistory({
          type: action.type,
          state: newState,
        }),
      },
      callback
    );
  };
  revertTo = (index, callback) => {
    const state = this.state.history[index].state;
    this.setState(
      {
        currentState: state,
        triggers: StatechartHelper.getAvailableTriggers(state),
        history: this.updateHistory({
          type: HISTORY_UNDO_TO,
          index: index,
        }),
      },
      callback
    );
  };
  //  undo(callback)
  //  callback for setState
  //  return true when actually undo something, false when nothing happened.
  undo = callback => {
    if (this.state.history.length > 1) {
      this.revertTo(1, callback);
      return true;
    } else {
      // callback();
      return false;
    }
  };
  restart = callback => {
    const state = this.machine.initialState;
    this.setState(
      {
        currentState: state,
        triggers: StatechartHelper.getAvailableTriggers(state),
        history: this.updateHistory({
          type: HISTORY_CLEAR,
        }),
      },
      callback
    );
  };
  //
  // Lifecycle Methods
  //
  componentDidMount() {
    document.querySelector("body").classList.add("wizard");
  }
  componentWillUnmount() {
    document.querySelector("body").classList.remove("wizard");
  }
  renderHistoryView() {
    return (
      <Row>
        <Col>
          <HistoryView
            reverse={true}
            history={this.state.history}
            revertTo={this.revertTo}
            dispatch={this.dispatch}
          />
        </Col>
      </Row>
    );
  }
  render() {
    const s = this.state;
    const settings = StatechartHelper.getData(s.currentState);
    const copy = this.context.copy;
    const content = this.context.copy.getContent(s.currentState);
    return (
      <Container>
        <Row className="my2 mmt1 mmb1">
          <Col className="py1">
            <Link to="/" style={{ marginLeft: -10 }}>
              <Logo variation="light" className="m0 p0" />
            </Link>
          </Col>
          <Col className="py2" />
        </Row>
        <Row style={{ justifyContent: "center" }}>
          <Card lg={7}>
            <UndoButton
              disabled={this.state.history.length === 1}
              onClick={() => this.undo()}
            >
              <img
                src={icoBack}
                alt="Undo"
                className="hover-invert pointer mx1"
                style={{ marginRight: 12 }}
              />
            </UndoButton>
            <Link to="/">
              <CloseButton>
                <img
                  src={icoClose}
                  alt="Close"
                  className="hover-invert pointer mx1"
                />
              </CloseButton>
            </Link>
            <Row className="mt3 center">
              <Col className="py1">
                <h3 className="h4 color-tt line-height-3">{content.title}</h3>
                {(content.productImage || content.productName) && (
                  <Row className={content.productImage ? "my2 mmy1" : "m0"}>
                    <Col md={8} lg={8} offset={{ md: 2, lg: 2 }}>
                      <img
                        src={content.productImage}
                        alt={content.productName}
                        className="w-100"
                      />
                    </Col>
                  </Row>
                )}
                <h3 className="h4 color-navy line-height-2">
                  {content.productName}
                </h3>
                {content.body && (
                  <div className="color-bg mt3">
                    <Markdown>{content.body}</Markdown>
                  </div>
                )}
              </Col>
            </Row>
            {content.productLinkUrl ? (
              <Row className="mt3 center">
                <Col className="">
                  <a
                    target="_blank"
                    className="btn"
                    href={content.productLinkUrl}
                  >
                    {content.productLinkText || "Jetzt kaufen"}
                  </a>
                </Col>
              </Row>
            ) : null}
            <Row className="mt2 mb2 center">
              <Col
                className={classnames(
                  "flex flex-wrap justify-center",
                  `trigger-count-${s.triggers ? s.triggers.length : 0}`
                )}
              >
                {!settings.showComparableProducts
                  ? null
                  : ComparableProducts.render({
                      dispatch: this.dispatch,
                      type: settings.showComparableProducts,
                    })}
                {s.triggers.map(trigger => (
                  <button
                    key={trigger}
                    className="btn"
                    foo={trigger}
                    onClick={() =>
                      this.dispatch({
                        type: trigger,
                      })
                    }
                  >
                    {copy.getTriggerName(s.currentState, trigger)}
                  </button>
                ))}
              </Col>
            </Row>
            {(content.sidenote1 || content.sidenote2) && (
              <Row style={{ color: color.battleship_grey, marginBottom: 15 }}>
                <Col>
                  <Markdown>{content.sidenote1}</Markdown>
                  <Markdown>{content.sidenote2}</Markdown>
                </Col>
              </Row>
            )}
          </Card>
        </Row>
      </Container>
    );
  }
}

export default withCopy(
  {
    locales: {
      en: "Wizard/en.js",
      de: "Wizard/de.js",
    },
  },
  Wizard
);



// WEBPACK FOOTER //
// ./src/Wizard/Wizard.js